import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import {
  Sparkles, Send, Loader2, MessageSquare, BookOpen, ArrowLeft,
  CheckCircle2, AlertTriangle, Clock, Bot,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_TONE = {
  bug: 'bg-red-50 text-red-700 border-red-200',
  question: 'bg-blue-50 text-blue-700 border-blue-200',
  feature: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  billing: 'bg-amber-50 text-amber-800 border-amber-200',
  integration: 'bg-violet-50 text-violet-800 border-violet-200',
  other: 'bg-slate-50 text-slate-700 border-slate-200',
};

const PRIORITY_TONE = {
  P0: 'bg-red-600 text-white',
  P1: 'bg-amber-500 text-white',
  P2: 'bg-slate-400 text-white',
  P3: 'bg-slate-300 text-slate-700',
};

const STATUS_LABEL = {
  new: 'New', triaged: 'Triaged', in_progress: 'In progress',
  awaiting_user: 'Awaiting you', resolved: 'Resolved', closed: 'Closed',
};

export default function SupportCenter() {
  const { user, token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [kb, setKb] = useState([]);
  const [selected, setSelected] = useState(null); // {kind:'article'|'ticket', item}
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [reply, setReply] = useState('');
  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const load = useCallback(async () => {
    try {
      const [kbRes, tRes] = await Promise.all([
        axios.get(`${API}/support/kb?limit=25`),
        token
          ? axios.get(`${API}/support/tickets?limit=25`, auth)
          : Promise.resolve({ data: { tickets: [] } }),
      ]);
      setKb(kbRes.data.articles || []);
      setTickets(tRes.data.tickets || []);
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim() || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/support/tickets`,
        { subject: subject.trim(), description: description.trim() },
        auth,
      );
      setSubject(''); setDescription('');
      await load();
      setSelected({ kind: 'ticket', item: res.data });
    } catch (err) {
      /* silent — will still be logged server-side */
    } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected || selected.kind !== 'ticket' || busy) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/support/tickets/${selected.item.id}/messages`,
        { body: reply.trim() },
        auth,
      );
      setReply('');
      setSelected({ kind: 'ticket', item: res.data });
      await load();
    } finally { setBusy(false); }
  };

  const openArticle = async (slug) => {
    try {
      const r = await axios.get(`${API}/support/kb/${slug}`);
      setSelected({ kind: 'article', item: r.data });
    } catch { /* silent */ }
  };

  const openTicket = async (id) => {
    try {
      const r = await axios.get(`${API}/support/tickets/${id}`, auth);
      setSelected({ kind: 'ticket', item: r.data });
    } catch { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-cn-bg-dark text-cn-text" data-testid="support-center-page">
      {/* Top bar */}
      <div className="border-b border-cn-line bg-white/70 dark:bg-slate-900/60 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <CaraLoomLogo />
            <span className="text-lg font-semibold tracking-tight">Support</span>
          </Link>
          <Link to={user ? '/dashboard' : '/'} className="text-sm text-cn-text-muted hover:text-cn-text flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* LEFT — KB + my tickets nav */}
        <div className="space-y-6">
          <div className="cn-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-cn-text-muted" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted">
                Knowledge base
              </h2>
            </div>
            <ul className="space-y-1.5">
              {kb.map((a) => (
                <li key={a.slug}>
                  <button
                    className="text-left text-sm hover:text-violet-700 hover:underline block w-full truncate"
                    onClick={() => openArticle(a.slug)}
                    data-testid={`support-kb-link-${a.slug}`}
                  >
                    {a.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {user && (
            <div className="cn-card p-5" data-testid="support-my-tickets">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-cn-text-muted" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted">
                  My tickets ({tickets.length})
                </h2>
              </div>
              {tickets.length === 0 && (
                <p className="text-xs text-cn-text-muted italic">
                  No tickets yet. Submit one on the right when you need help.
                </p>
              )}
              <ul className="space-y-2">
                {tickets.map((t) => (
                  <li key={t.id}>
                    <button
                      className="block w-full text-left rounded-lg border border-cn-line hover:border-violet-300 hover:bg-violet-50/30 dark:hover:bg-slate-800/60 p-2 transition-colors"
                      onClick={() => openTicket(t.id)}
                      data-testid={`support-ticket-item-${t.id}`}
                    >
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <span className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded ${PRIORITY_TONE[t.priority] || PRIORITY_TONE.P2}`}>
                          {t.priority}
                        </span>
                        <span className={`text-[0.6rem] uppercase px-1.5 py-0.5 rounded border ${CATEGORY_TONE[t.category] || CATEGORY_TONE.other}`}>
                          {t.category}
                        </span>
                        <span className="text-[0.6rem] text-cn-text-muted ml-auto">
                          {STATUS_LABEL[t.status] || t.status}
                        </span>
                      </div>
                      <div className="text-sm font-medium leading-snug truncate">
                        {t.subject}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT — form OR selected item */}
        <div className="md:col-span-2 space-y-6">
          {selected?.kind === 'article' && (
            <div className="cn-card p-6" data-testid="support-article-view">
              <button
                className="text-xs text-cn-text-muted hover:text-cn-text mb-3 flex items-center gap-1"
                onClick={() => setSelected(null)}
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <h1 className="text-2xl font-semibold tracking-tight mb-2">
                {selected.item.title}
              </h1>
              <div className="text-xs text-cn-text-muted mb-4 flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded border text-[0.65rem] uppercase ${CATEGORY_TONE[selected.item.category] || CATEGORY_TONE.other}`}>
                  {selected.item.category}
                </span>
                <span>·</span>
                <span>{(selected.item.tags || []).slice(0, 3).join(' · ')}</span>
              </div>
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <ReactMarkdown>{selected.item.body_md || ''}</ReactMarkdown>
              </div>
            </div>
          )}

          {selected?.kind === 'ticket' && (
            <div className="cn-card p-6" data-testid="support-ticket-view">
              <button
                className="text-xs text-cn-text-muted hover:text-cn-text mb-3 flex items-center gap-1"
                onClick={() => setSelected(null)}
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${PRIORITY_TONE[selected.item.priority] || PRIORITY_TONE.P2}`}>
                  {selected.item.priority}
                </span>
                <span className={`text-xs uppercase px-1.5 py-0.5 rounded border ${CATEGORY_TONE[selected.item.category] || CATEGORY_TONE.other}`}>
                  {selected.item.category}
                </span>
                <span className="text-xs text-cn-text-muted">
                  · {STATUS_LABEL[selected.item.status] || selected.item.status}
                </span>
                {selected.item.resolved_at && (
                  <span className="text-xs text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Resolved
                  </span>
                )}
              </div>
              <h1 className="text-xl font-semibold tracking-tight mb-4">
                {selected.item.subject}
              </h1>
              <div className="space-y-3">
                {(selected.item.messages || []).map((m) => (
                  <div
                    key={m.id}
                    className={`rounded-xl px-4 py-3 border ${
                      m.from_role === 'cara' || m.from_role === 'admin'
                        ? 'bg-violet-50/50 border-violet-200 dark:bg-slate-800/60'
                        : 'bg-slate-50 border-cn-line dark:bg-slate-900/50'
                    }`}
                  >
                    <div className="text-[0.65rem] uppercase tracking-wider text-cn-text-muted mb-1 flex items-center gap-1.5">
                      {m.from_role === 'cara' ? (
                        <><Bot className="w-3 h-3 text-violet-600" /> Cara (AI)</>
                      ) : m.from_role === 'admin' ? (
                        'CaraLoom support'
                      ) : (
                        'You'
                      )}
                      {m.confidence && (
                        <span className="ml-1 text-violet-700">
                          · confidence {(m.confidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">
                      {m.body}
                    </div>
                  </div>
                ))}
              </div>
              {selected.item.status !== 'closed' && (
                <div className="mt-4 flex gap-2">
                  <input
                    type="text"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendReply()}
                    placeholder="Write a reply…"
                    className="flex-1 rounded-lg border border-cn-line bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                    data-testid="support-ticket-reply-input"
                  />
                  <button
                    onClick={sendReply}
                    disabled={busy || !reply.trim()}
                    className="cn-btn cn-btn-primary"
                    data-testid="support-ticket-reply-send"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send
                  </button>
                </div>
              )}
            </div>
          )}

          {!selected && (
            <div className="cn-card p-6" data-testid="support-new-ticket">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-600" />
                <h2 className="text-sm font-semibold uppercase tracking-wider text-cn-text-muted">
                  Open a support ticket
                </h2>
              </div>
              <p className="text-sm text-cn-text-muted mb-4">
                Cara (our AI assistant) reads every ticket the moment it lands.
                Common questions are answered instantly — the rest go straight to
                our support team with the LLM's draft already attached.
              </p>
              {!user && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    You're browsing without an account. Sign in to submit a
                    ticket — the auto-triage engine works best when it can see
                    your role, org, and any referrals you reference.
                  </span>
                </div>
              )}
              <form onSubmit={submit} className="space-y-3">
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject — what happened, in one line"
                  maxLength={200}
                  required
                  disabled={!user || busy}
                  className="w-full rounded-lg border border-cn-line bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                  data-testid="support-new-subject"
                />
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what you tried, what happened, and what you expected. Include a referral or trace ID if applicable — never patient names or DOBs."
                  minLength={5}
                  maxLength={4000}
                  required
                  disabled={!user || busy}
                  rows={6}
                  className="w-full rounded-lg border border-cn-line bg-white dark:bg-slate-900 px-3 py-2 text-sm"
                  data-testid="support-new-description"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-cn-text-muted flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Avg auto-response: ~4 seconds
                  </span>
                  <button
                    type="submit"
                    disabled={!user || busy || !subject.trim() || !description.trim()}
                    className="cn-btn cn-btn-primary"
                    data-testid="support-new-submit"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Submit ticket
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
